import { ChevronDown, ChevronUp, Edit, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import type { JobType } from "@/hooks/use-job-types";
import {
  useDeleteJobType,
  useJobTypes,
  useReorderJobTypes,
  useUpdateJobType,
} from "@/hooks/use-job-types";
import { DeleteEntityDialog } from "./DeleteEntityDialog";
import { EditJobTypeDialog, NewJobTypeDialog } from "./JobTypeDialogs";

type DialogState =
  | { type: "none" }
  | { type: "create" }
  | { type: "edit"; jobType: JobType }
  | { type: "delete"; jobType: JobType };

export function JobTypesAdmin() {
  const [dialog, setDialog] = useState<DialogState>({ type: "none" });

  const { data: jobTypes = [], isLoading, isError, error, refetch } = useJobTypes();
  const updateJobType = useUpdateJobType();
  const deleteJobType = useDeleteJobType();
  const reorderJobTypes = useReorderJobTypes();

  function closeDialog() {
    setDialog({ type: "none" });
  }

  function moveUp(index: number) {
    if (index === 0) return;
    const ids = jobTypes.map((jt) => jt.id);
    const prev = ids[index - 1];
    const curr = ids[index];
    if (!prev || !curr) return;
    const newOrder = [...ids];
    newOrder[index - 1] = curr;
    newOrder[index] = prev;
    reorderJobTypes.mutate(newOrder);
  }

  function moveDown(index: number) {
    if (index >= jobTypes.length - 1) return;
    const ids = jobTypes.map((jt) => jt.id);
    const next = ids[index + 1];
    const curr = ids[index];
    if (!next || !curr) return;
    const newOrder = [...ids];
    newOrder[index + 1] = curr;
    newOrder[index] = next;
    reorderJobTypes.mutate(newOrder);
  }

  function toggleActive(jt: JobType) {
    updateJobType.mutate({ id: jt.id, active: !jt.active });
  }

  function handleDeleteConfirm(jt: JobType) {
    deleteJobType.mutate(jt.id, { onSuccess: closeDialog });
  }

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load job types"}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="h-section">Job Types</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Manage job type codes used to classify time entries.
          </p>
        </div>
        <Button onClick={() => setDialog({ type: "create" })}>
          <Plus className="mr-2 h-4 w-4" />
          Add Job Type
        </Button>
      </div>

      <DataTable
        headers={[
          { label: "Code", className: "w-24" },
          { label: "Label" },
          { label: "Status", className: "w-28" },
          { label: "Order", className: "w-28 text-right" },
          { label: "Actions", className: "w-28 text-right" },
        ]}
        loading={isLoading}
        empty={
          <EmptyState
            icon={Plus}
            title="No job types"
            description="Add your first job type to get started."
          />
        }
      >
        {jobTypes.map((jt, index) => (
          <tr
            key={jt.id}
            className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
          >
            <td className="px-4 py-3">
              <span className={`chip-jobtype chip-jobtype--${jt.code}`}>{jt.code}</span>
            </td>
            <td className="px-4 py-3 font-medium">{jt.label}</td>
            <td className="px-4 py-3">
              <button
                type="button"
                onClick={() => toggleActive(jt)}
                disabled={updateJobType.isPending}
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium transition-colors ${
                  jt.active
                    ? "bg-green-100 text-green-800 hover:bg-green-200"
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
                title={jt.active ? "Click to deactivate" : "Click to activate"}
              >
                {jt.active ? "Active" : "Inactive"}
              </button>
            </td>
            <td className="px-4 py-3 text-right">
              <div className="flex items-center justify-end gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  disabled={index === 0 || reorderJobTypes.isPending}
                  onClick={() => moveUp(index)}
                  title="Move up"
                >
                  <ChevronUp className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  disabled={index === jobTypes.length - 1 || reorderJobTypes.isPending}
                  onClick={() => moveDown(index)}
                  title="Move down"
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </div>
            </td>
            <td className="px-4 py-3 text-right">
              <div className="flex items-center justify-end gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => setDialog({ type: "edit", jobType: jt })}
                  title="Edit"
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive hover:text-destructive"
                  onClick={() => setDialog({ type: "delete", jobType: jt })}
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </td>
          </tr>
        ))}
      </DataTable>

      <NewJobTypeDialog
        open={dialog.type === "create"}
        onOpenChange={(open) => !open && closeDialog()}
      />

      {dialog.type === "edit" && (
        <EditJobTypeDialog
          key={dialog.jobType.id}
          open
          onOpenChange={(open) => !open && closeDialog()}
          jobType={dialog.jobType}
        />
      )}

      {dialog.type === "delete" && (
        <DeleteEntityDialog
          open
          onOpenChange={(open) => !open && closeDialog()}
          entityType="job type"
          entityName={`${dialog.jobType.code} — ${dialog.jobType.label}`}
          isPending={deleteJobType.isPending}
          onConfirm={() => handleDeleteConfirm(dialog.jobType)}
        />
      )}
    </div>
  );
}
